// src/services/dashboardService.ts
import axios from 'axios';
import { API_CONFIG, getApiUrl } from '../config/api';

export interface ScoreHistory {
  month: string;
  score: number;
}

export interface EngagementMetric {
  name: string;
  value: number | string;
  unit?: string;
  changePercent: number;
}

export interface SimilarArtist {
  id: number;
  name: string;
  genre: string;
  score: number;
  avatar: string;
}

export interface MonthlyMetric {
  month: string;
  streams: number;
  listeners: number;
  saves: number;
  followers: number;
  playlists?: number;
  score: number;
}

export interface DashboardSummary {
  score: number;
  previousScore: number;
  followers: number;
  monthlyListeners: number;
  totalStreams: number;
  saveRate: string | number;
  scoreHistory: ScoreHistory[];
  engagement: EngagementMetric[];
  similarArtists: SimilarArtist[];
  monthlyMetrics?: MonthlyMetric[]; // Add this field to the interface
}

// Custom error for authentication errors
export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

// Fetch complete dashboard summary
export const getDashboardSummary = async (): Promise<DashboardSummary> => {
  try {
    const response = await axios.get(getApiUrl(API_CONFIG.ENDPOINTS.DASHBOARD.SUMMARY));
    
    // Check if the API returned an error about the user ID
    if (response.data && response.data.error === 'No User ID found') {
      throw new AuthError('User not authenticated');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard summary:', error);
    
    // Rethrow AuthError for authentication issues
    if (error instanceof AuthError) {
      throw error;
    }
    
    throw error;
  }
};

// Fetch only score history
export const getScoreHistory = async (): Promise<ScoreHistory[]> => {
  try {
    const response = await axios.get(getApiUrl(API_CONFIG.ENDPOINTS.DASHBOARD.SCORE_HISTORY));
    
    // Check if the API returned an error about the user ID
    if (response.data && response.data.error === 'No User ID found') {
      throw new AuthError('User not authenticated');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching score history:', error);
    
    // Rethrow AuthError for authentication issues
    if (error instanceof AuthError) {
      throw error;
    }
    
    throw error;
  }
};

// Fetch only engagement metrics
export const getEngagementMetrics = async (): Promise<EngagementMetric[]> => {
  try {
    const response = await axios.get(getApiUrl(API_CONFIG.ENDPOINTS.DASHBOARD.ENGAGEMENT_METRICS));
    
    // Check if the API returned an error about the user ID
    if (response.data && response.data.error === 'No User ID found') {
      throw new AuthError('User not authenticated');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching engagement metrics:', error);
    
    // Rethrow AuthError for authentication issues
    if (error instanceof AuthError) {
      throw error;
    }
    
    throw error;
  }
};

// Fetch only similar artists
export const getSimilarArtists = async (): Promise<SimilarArtist[]> => {
  try {
    const response = await axios.get(getApiUrl(API_CONFIG.ENDPOINTS.DASHBOARD.SIMILAR_ARTISTS));
    
    // Check if the API returned an error about the user ID
    if (response.data && response.data.error === 'No User ID found') {
      throw new AuthError('User not authenticated');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching similar artists:', error);
    
    // Rethrow AuthError for authentication issues
    if (error instanceof AuthError) {
      throw error;
    }
    
    throw error;
  }
};